import React, { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { useLanguage } from "@/contexts/LanguageContext";
import { translate } from "@/utils/translations";
import { getPlatformMetrics, generate24hThroughputData, formatLargeNumber } from "@/utils/platformMetrics";
import {
  Network,
  Activity,
  Zap,
  Users,
  Info
} from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  ResponsiveContainer
} from "recharts";

const Superbrain = () => {
  const { language } = useLanguage();
  const [metrics, setMetrics] = useState(null);
  const [chartData, setChartData] = useState([]);
  
  const t = (text) => translate(text, language);

  useEffect(() => {
    // Get consistent platform metrics
    const platformMetrics = getPlatformMetrics(71); // 71 virtual nodes currently
    setMetrics(platformMetrics);
    
    // Get deterministic 24h chart data (stays constant for the day)
    const throughputData = generate24hThroughputData();
    setChartData(throughputData);
    
    // Update validators periodically (realistic fluctuation)
    const interval = setInterval(() => {
      setMetrics(prev => {
        if (!prev) return null;
        const jitter = Math.floor((Math.random() - 0.5) * 10);
        return {
          ...prev,
          activeValidators: Math.max(120, Math.min(1200, prev.activeValidators + jitter))
        };
      });
    }, 45000); // Every 45 seconds
    
    return () => clearInterval(interval);
  }, []);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="glass rounded-lg p-3 border border-cyan-500/20">
          <p className="text-xs text-gray-400">{payload[0].payload.time}</p>
          <p className="text-sm font-bold text-cyan-400 font-mono">
            {payload[0].value.toLocaleString()} TPS
          </p>
        </div>
      );
    }
    return null;
  };

  if (!metrics) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-gray-400">Loading network metrics...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="superbrain-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold text-white flex items-center gap-3">
            <Network className="w-8 h-8 text-[#00D2FF]" />
            {t("Superbrain Network")}
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            {t("Global decentralized intelligence infrastructure")}
          </p>
        </div>
        <Badge className="bg-[#00FF94]/10 text-[#00FF94] border-[#00FF94]/20 font-mono">
          <span className="w-2 h-2 rounded-full bg-[#00FF94] mr-2 animate-pulse" />
          {t("OPERATIONAL")}
        </Badge>
      </div>

      {/* Top Metric Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Total Nodes */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Card className="bg-[#0A0A0A] border-white/10 hover:border-cyan-500/30 transition-all cursor-help">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-[#00D2FF]/10 flex items-center justify-center">
                      <Network className="w-5 h-5 text-[#00D2FF]" />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-white font-mono animate-fade-in">
                    {metrics.totalNodes.toLocaleString()}
                  </p>
                  <p className="text-sm text-gray-500">{t("Total Nodes")}</p>
                </CardContent>
              </Card>
            </TooltipTrigger>
            <TooltipContent className="bg-[#0A0A0A] border-cyan-500/30">
              <p className="text-xs">Total registered robot and compute nodes</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Active Validators */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Card className="bg-[#0A0A0A] border-white/10 hover:border-green-500/30 transition-all cursor-help">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-[#00FF94]/10 flex items-center justify-center">
                      <Users className="w-5 h-5 text-[#00FF94]" />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-white font-mono transition-all duration-500">
                    {metrics.activeValidators.toLocaleString()}
                  </p>
                  <p className="text-sm text-gray-500">{t("Active Validators")}</p>
                </CardContent>
              </Card>
            </TooltipTrigger>
            <TooltipContent className="bg-[#0A0A0A] border-green-500/30">
              <p className="text-xs">Validators currently participating in consensus</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Total Inference */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Card className="bg-[#0A0A0A] border-white/10 hover:border-purple-500/30 transition-all cursor-help">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                      <Zap className="w-5 h-5 text-purple-400" />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-white font-mono animate-fade-in">
                    {formatLargeNumber(metrics.totalInference)}
                  </p>
                  <p className="text-sm text-gray-500">{t("Total Inference")}</p>
                </CardContent>
              </Card>
            </TooltipTrigger>
            <TooltipContent className="bg-[#0A0A0A] border-purple-500/30">
              <p className="text-xs">Total verified inference receipts</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Network TPS */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Card className="bg-[#0A0A0A] border-white/10 hover:border-orange-500/30 transition-all cursor-help">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center">
                      <Activity className="w-5 h-5 text-orange-400" />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-white font-mono animate-fade-in">
                    {metrics.networkTPS.toLocaleString()}
                  </p>
                  <p className="text-sm text-gray-500">{t("Network TPS")}</p>
                </CardContent>
              </Card>
            </TooltipTrigger>
            <TooltipContent className="bg-[#0A0A0A] border-orange-500/30">
              <p className="text-xs">Network throughput indicator aligned with the 24h curve</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* 24h Throughput Chart */}
      <Card className="bg-[#0A0A0A] border-white/10">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg text-white">{t("Network Throughput (24h)")}</h3>
          </div>
          
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="colorTPS" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#00D2FF" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#00D2FF" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
              <XAxis 
                dataKey="time" 
                stroke="#888" 
                tick={{ fill: '#888', fontSize: 11 }}
                interval="preserveStartEnd"
              />
              <YAxis 
                stroke="#888" 
                tick={{ fill: '#888', fontSize: 11 }}
                domain={[1500, 5500]}
              />
              <ChartTooltip content={<CustomTooltip />} />
              <Area 
                type="monotone" 
                dataKey="tps" 
                stroke="#00D2FF" 
                strokeWidth={3}
                fillOpacity={1} 
                fill="url(#colorTPS)" 
                animationDuration={2500}
                animationEasing="ease-in-out"
              />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Additional Analytics Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Regional Distribution */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardContent className="p-6">
            <h3 className="font-heading text-lg text-white mb-4 flex items-center gap-2">
              <Network className="w-5 h-5 text-cyan-400" />
              {t("Regional Distribution")}
            </h3>
            
            <div className="space-y-3">
              {[
                { region: "North America", nodes: Math.floor(metrics.totalNodes * 0.35), color: "#00D2FF" },
                { region: "Europe", nodes: Math.floor(metrics.totalNodes * 0.28), color: "#00FF94" },
                { region: "Asia Pacific", nodes: Math.floor(metrics.totalNodes * 0.22), color: "#A855F7" },
                { region: "South America", nodes: Math.floor(metrics.totalNodes * 0.09), color: "#F97316" },
                { region: "Africa", nodes: Math.floor(metrics.totalNodes * 0.06), color: "#3B82F6" }
              ].map((item, idx) => (
                <div key={idx} className="space-y-1">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-300">{item.region}</span>
                    <span className="font-mono text-white">{item.nodes.toLocaleString()}</span>
                  </div>
                  <div className="w-full bg-white/5 rounded-full h-2 overflow-hidden">
                    <div 
                      className="h-full rounded-full transition-all duration-1000"
                      style={{ 
                        width: `${(item.nodes / metrics.totalNodes) * 100}%`,
                        backgroundColor: item.color
                      }}
                    />
                  </div>
                  <div className="flex justify-end text-xs text-gray-500">
                    {((item.nodes / metrics.totalNodes) * 100).toFixed(1)}%
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Network Activity Feed */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardContent className="p-6">
            <h3 className="font-heading text-lg text-white mb-4 flex items-center gap-2">
              <Activity className="w-5 h-5 text-green-400" />
              Recent Network Activity
            </h3>
            
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {[
                { type: "Inference", node: "Virtual Humanoid Alpha", time: "2s ago", color: "cyan" },
                { type: "Proof Generated", node: "Virtual Humanoid Gamma", time: "5s ago", color: "purple" },
                { type: "Agent Deployed", node: "Virtual Humanoid Beta", time: "12s ago", color: "green" },
                { type: "Validation Complete", node: "Virtual Humanoid Delta", time: "18s ago", color: "orange" },
                { type: "Inference", node: "Virtual Humanoid Epsilon", time: "23s ago", color: "cyan" },
                { type: "Training Epoch", node: "Virtual Humanoid Zeta", time: "31s ago", color: "blue" },
                { type: "Proof Generated", node: "Virtual Humanoid Eta", time: "37s ago", color: "purple" },
                { type: "Safety Check", node: "Virtual Humanoid Theta", time: "45s ago", color: "orange" },
                { type: "Inference", node: "Virtual Humanoid Iota", time: "52s ago", color: "cyan" },
                { type: "Agent Deployed", node: "Virtual Humanoid Kappa", time: "1m ago", color: "green" }
              ].map((activity, idx) => (
                <div key={idx} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 transition-colors">
                  <div className={`w-2 h-2 rounded-full ${
                    activity.color === 'cyan' ? 'bg-cyan-400' :
                    activity.color === 'purple' ? 'bg-purple-400' :
                    activity.color === 'green' ? 'bg-green-400' :
                    activity.color === 'orange' ? 'bg-orange-400' :
                    'bg-blue-400'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">{activity.type}</p>
                    <p className="text-xs text-gray-500 truncate">{activity.node}</p>
                  </div>
                  <span className="text-xs text-gray-500 whitespace-nowrap">{activity.time}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Performance Metrics Table */}
      <Card className="bg-[#0A0A0A] border-white/10">
        <CardContent className="p-6">
          <h3 className="font-heading text-lg text-white mb-4 flex items-center gap-2">
            <Zap className="w-5 h-5 text-yellow-400" />
            Network Performance Metrics
          </h3>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-white/5 rounded-lg border border-white/5">
              <p className="text-xs text-gray-500 mb-1">Avg Block Time</p>
              <p className="text-2xl font-bold text-cyan-400 font-mono">2.1s</p>
            </div>
            <div className="p-4 bg-white/5 rounded-lg border border-white/5">
              <p className="text-xs text-gray-500 mb-1">Network Latency</p>
              <p className="text-2xl font-bold text-green-400 font-mono">34ms</p>
            </div>
            <div className="p-4 bg-white/5 rounded-lg border border-white/5">
              <p className="text-xs text-gray-500 mb-1">Success Rate</p>
              <p className="text-2xl font-bold text-purple-400 font-mono">99.8%</p>
            </div>
            <div className="p-4 bg-white/5 rounded-lg border border-white/5">
              <p className="text-xs text-gray-500 mb-1">Consensus Time</p>
              <p className="text-2xl font-bold text-orange-400 font-mono">1.8s</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Node Type Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardContent className="p-6">
            <h3 className="font-heading text-lg text-white mb-4">Node Type Distribution</h3>
            
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-300">Humanoid Robots</span>
                  <span className="text-sm font-mono text-cyan-400">{Math.floor(metrics.totalNodes * 0.65).toLocaleString()}</span>
                </div>
                <div className="w-full bg-white/5 rounded-full h-2">
                  <div className="h-full bg-gradient-to-r from-cyan-500 to-cyan-400 rounded-full" style={{ width: '65%' }} />
                </div>
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-300">Edge Compute</span>
                  <span className="text-sm font-mono text-purple-400">{Math.floor(metrics.totalNodes * 0.25).toLocaleString()}</span>
                </div>
                <div className="w-full bg-white/5 rounded-full h-2">
                  <div className="h-full bg-gradient-to-r from-purple-500 to-purple-400 rounded-full" style={{ width: '25%' }} />
                </div>
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-300">Validators</span>
                  <span className="text-sm font-mono text-green-400">{Math.floor(metrics.totalNodes * 0.10).toLocaleString()}</span>
                </div>
                <div className="w-full bg-white/5 rounded-full h-2">
                  <div className="h-full bg-gradient-to-r from-green-500 to-green-400 rounded-full" style={{ width: '10%' }} />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Consensus Stats */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardContent className="p-6">
            <h3 className="font-heading text-lg text-white mb-4">Consensus Statistics</h3>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                <span className="text-sm text-gray-300">Blocks Finalized (24h)</span>
                <span className="text-lg font-bold text-cyan-400 font-mono">41,234</span>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                <span className="text-sm text-gray-300">Validator Uptime</span>
                <span className="text-lg font-bold text-green-400 font-mono">99.97%</span>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                <span className="text-sm text-gray-300">Missed Blocks</span>
                <span className="text-lg font-bold text-orange-400 font-mono">12</span>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                <span className="text-sm text-gray-300">Avg Validator Stake</span>
                <span className="text-lg font-bold text-purple-400 font-mono">2.4K</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Network Health Indicators */}
      <Card className="bg-gradient-to-br from-[#0A0A0A] to-cyan-950/20 border-cyan-500/20">
        <CardContent className="p-6">
          <h3 className="font-heading text-lg text-white mb-6 flex items-center gap-2">
            <Activity className="w-5 h-5 text-cyan-400" />
            Network Health Indicators
          </h3>
          
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-green-400 mb-1">✓</div>
              <p className="text-xs text-gray-400">Consensus</p>
              <p className="text-sm font-semibold text-green-400">Healthy</p>
            </div>
            
            <div className="text-center">
              <div className="text-3xl font-bold text-green-400 mb-1">✓</div>
              <p className="text-xs text-gray-400">Network Sync</p>
              <p className="text-sm font-semibold text-green-400">Synced</p>
            </div>
            
            <div className="text-center">
              <div className="text-3xl font-bold text-cyan-400 mb-1">◉</div>
              <p className="text-xs text-gray-400">Validator Participation</p>
              <p className="text-sm font-semibold text-cyan-400">98.5%</p>
            </div>
            
            <div className="text-center">
              <div className="text-3xl font-bold text-green-400 mb-1">✓</div>
              <p className="text-xs text-gray-400">P2P Connectivity</p>
              <p className="text-sm font-semibold text-green-400">Optimal</p>
            </div>
            
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-400 mb-1">▲</div>
              <p className="text-xs text-gray-400">Network Growth</p>
              <p className="text-sm font-semibold text-purple-400">+2.3%</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Superbrain;
